import React, { useState, useEffect } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    TableContainer,
    IconButton,
    Pagination,
    Tooltip,
    Switch,
    Button as MuiButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { FaPlus, FaCheck } from "react-icons/fa";
import { useFormik } from "formik";
import Button from "../components/common/styled components/Button";
import * as Yup from "yup";
import api from "../axios/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminMaster = () => {
    const [open, setOpen] = useState(false);
    const [paginationSettings, setPaginationSettings] = useState([]);
    const [defaultSetting, setDefaultSetting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    //  Fetch pagination settings
    const fetchPaginationSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get("api/upload/getPaginationSettings");
            if (res.data.status) {
                setPaginationSettings(res.data.data.options);
                setDefaultSetting(res.data.data.default);
            } else {
                toast.info("No pagination settings found");
            }
        } catch (error) {
            toast.error("Error fetching pagination settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPaginationSettings();
    }, []);

    //  Delete handler (unchanged logic)
    const handleDelete = async (id) => {
        try {
            const record = paginationSettings.find((item) => item.id === id);
            const isDefault =
                record?.is_default === true ||
                record?.is_default === 1 ||
                record?.is_default === "1" ||
                record?.isDefault === true ||
                record?.isDefault === 1 ||
                record?.isDefault === "1";

            if (isDefault) {
                toast.warning("You cannot delete the default pagination setting. Please turn off mark as default first.");
                setDeleteModalOpen(false);
                return;
            }

            const res = await api.delete(`/api/upload/deletePaginationSetting/${id}`);

            if (res.data.success) {
                toast.success("Deleted successfully!");
                setPaginationSettings((prev) => prev.filter((item) => item.id !== id));
            } else {
                toast.warning(res.data.message);
            }
        } catch (error) {
            toast.error("Server error while deleting pagination setting.");
        } finally {
            setDeleteModalOpen(false);
        }
    };

    const handleStatusChange = async (id, newDefault) => {
        try {
            const res = await api.put(`/api/upload/updatePaginationStatus/${id}`, {
                isDefault: newDefault,
            });

            if (res.data.success) {
                toast.success(res.data.message);
                fetchPaginationSettings();
            } else {
                toast.error(res.data.message);
            }
        } catch (error) {
            console.error(" Error updating default status:", error);
        }
    };

    const handleStatusChange1 = async (id, newStatus) => {
        setUpdatingId(id);
        try {
            const res = await api.put(`/api/upload/statuschange/${id}`, {
                isActive: newStatus,
            });

            if (res.data.success) {
                toast.success(res.data.message);
                await fetchPaginationSettings();
            } else {
                toast.error(res.data.message);
            }
        } catch (error) {
            console.error(" Error updating default status:", error);
        } finally {
            setUpdatingId(null);
        }
    };

    //  Formik setup
    const formik = useFormik({
        initialValues: {
            setting_value: "",
            is_default: false,
        },
        validationSchema: Yup.object({
            setting_value: Yup.string()
                .required("Setting value is required")
                .matches(/^\d+$/, "Only numbers are allowed"),
            is_default: Yup.boolean(),
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                const payload = {
                    setting_key: "rows_per_page",
                    setting_value: values.setting_value,
                    is_default: values.is_default ? 1 : 0,
                };

                const res = await api.post("api/upload/addPaginationSetting", payload);

                if (res.data.success) {
                    toast.success("Setting added successfully!");
                    resetForm();
                    setOpen(false);
                    fetchPaginationSettings();
                } else {
                    toast.warning("Failed to add setting!");
                }
            } catch (error) {
                toast.error(error);
                // toast.error("Server error while adding setting!");
            }
        },
    });

    //  Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
    };

    //  Paginate data
    const totalPages = Math.ceil(paginationSettings.length / itemsPerPage);
    const paginatedData = paginationSettings.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    return (
        <Box sx={{ p: 4 }}>
            <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
            <Box sx={{display: "flex",justifyContent: "space-between",mb: 3,gap: 2}}>
                <Typography variant="h4" gutterBottom>Pagination Master</Typography>
                <Button
                    onClick={() => setOpen(true)}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "10px 16px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                >
                    <FaPlus size="14px" />
                    Add New Pagination
                </Button>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    mt: 2,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
                    borderRadius: "8px",
                    overflow: "hidden",
                }}
            >
                {loading ? (
                    <Box sx={{ textAlign: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : paginationSettings.length > 0 ? (
                    <>
                        <Table>
                            <TableHead sx={{ backgroundColor: "#002F43" }}>
                                <TableRow>
                                    {["Sr no", "Row per page", "Default Status", "Status", "Actions"].map((head) => (
                                        <TableCell
                                            key={head}
                                            sx={{
                                                color: "white",
                                                fontWeight: "bold",
                                                textAlign: "center",
                                            }}
                                        >
                                            {head}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedData.map((item, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            "&:hover": { backgroundColor: "#f9f9f9" },
                                            transition: "0.3s",
                                        }}
                                    >
                                        <TableCell align="center">
                                            {index + 1 + (page - 1) * itemsPerPage}
                                        </TableCell>
                                        <TableCell align="center">{item.VALUE}</TableCell>

                                        {/*  Centered Switch */}
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    height: "100%",
                                                }}
                                            >
                                                <Switch
                                                    checked={item.isDefault === 1}
                                                    onChange={(e) => handleStatusChange(item.id, e.target.checked)}
                                                    title={item.isDefault === 1 ? "Remove From Default" : "Set as Default"}
                                                    disabled={item.isDefault}
                                                    sx={{
                                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                                            color: "green",
                                                        },
                                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                            backgroundColor: "green",
                                                            opacity: 1,
                                                        },
                                                        "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                                                            backgroundColor: "red",
                                                            opacity: 1,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    height: "100%",
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    data-tooltip-id="active"
                                                    title={item.isActive === 1 ? "Activte " : "Deactivate"}
                                                    data-tooltip-content={Number(item.isActive) === 0 ? "Activate" : "Deactivate"}
                                                    onClick={() => handleStatusChange1(item.id, Number(item.isActive) === 0 ? true : false)}
                                                    sx={{
                                                        bgcolor: "#f5f5f5",
                                                        "&:hover": {
                                                            bgcolor: Number(item.isActive) === 0 ? "#ffebee" : "#f1f8e9",
                                                        },
                                                    }}
                                                    disabled={updatingId === item.id || item.isDefault}
                                                >
                                                    <FaCheck
                                                        color={Number(item.isActive) === 0 ? "red" : "#83d300"}
                                                        size="18px"
                                                    />
                                                </IconButton>
                                            </Box>
                                        </TableCell>



                                        {/*  Centered Delete Icon */}
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setDeleteId(item.id);
                                                            setDeleteModalOpen(true);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>


                        {/* Pagination Controls */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 3,
                                mb: 3,
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                shape="round"
                                showFirstButton
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography sx={{ textAlign: "center", p: 2 }}>
                        No pagination settings found.
                    </Typography>
                )}
            </TableContainer>

            {/*  Delete Confirmation Dialog */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this pagination setting?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        type="button"
                        onClick={() => setDeleteModalOpen(false)}
                        variant="cancel"
                        sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "6px 16px",
                            backgroundColor: "#e0e0e0",
                            color: "#000",
                            "&:hover": {
                                backgroundColor: "#d5d5d5",
                            },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(deleteId)}
                        sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "6px 16px",
                            backgroundColor: "#d32f2f",
                            "&:hover": {
                                backgroundColor: "#b71c1c",
                            },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for Adding Pagination */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    Add Pagination No
                    <IconButton onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        onSubmit={formik.handleSubmit}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: 3,
                            mt: 2,
                        }}
                    >

                        <TextField
                            label="Rows per page value"
                            name="setting_value"
                            value={formik.values.setting_value}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="number"
                            error={formik.touched.setting_value && Boolean(formik.errors.setting_value)}
                            helperText={formik.touched.setting_value && formik.errors.setting_value}
                            InputProps={{
                                
                                style: {
                                    height: "50px",
                                    width: "195px",

                                },
                                inputProps: { min: 1, max: 99999, onKeyDown: (e) => {
                                  if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                                }, },
                                
                            }}
                        />


                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="is_default"
                                    checked={formik.values.is_default}
                                    onChange={formik.handleChange}
                                    sx={{
                                        color: "primary.main",
                                        "&.Mui-checked": { color: "primary.main" },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ color: "black", fontWeight: 500 }}>
                                    Mark as Default
                                </Typography>
                            }
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} variant="cancel">
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={formik.handleSubmit}>
                        Add Setting
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminMaster;